import { useEffect, useMemo, useState } from 'react';

interface UseTablePaginationResult<T> {
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  pagedRows: T[];
  setPage: (nextPage: number) => void;
  setPageSize: (nextPageSize: number) => void;
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export const useTablePagination = <T,>(
  rows: T[],
  initialPageSize = 10
): UseTablePaginationResult<T> => {
  const [page, setPage] = useState(1);
  const [pageSizeState, setPageSizeState] = useState(initialPageSize);
  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSizeState));

  useEffect(() => {
    if (page > pageCount) {
      setPage(pageCount);
    }
  }, [page, pageCount]);

  const setPageSize = (nextPageSize: number) => {
    setPageSizeState(nextPageSize);
    setPage(1);
  };

  const setSafePage = (nextPage: number) => {
    setPage(clamp(nextPage, 1, pageCount));
  };

  const pagedRows = useMemo(() => {
    const startIndex = (page - 1) * pageSizeState;
    return rows.slice(startIndex, startIndex + pageSizeState);
  }, [rows, page, pageSizeState]);

  return {
    total,
    page,
    pageSize: pageSizeState,
    pageCount,
    pagedRows,
    setPage: setSafePage,
    setPageSize
  };
};

